import { WORLD_VERSION } from '@cloudpearl/config';
import type { MapId } from '@cloudpearl/config';
import { normalizeSeedCode } from '@cloudpearl/core';
import type { SettingsState } from '../ui/Ui';

/**
 * All persistence goes through here; every access is guarded (private mode, quota, disabled storage).
 * Scores and replays are scoped to WORLD_VERSION (a new version means new worlds); settings, the
 * last map and the run count are not, so a world update never resets your preferences.
 */
const PREFIX = `cloudpearl:v${WORLD_VERSION}:`;
const PREFS = 'cloudpearl:';

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? ({ ...fallback, ...JSON.parse(raw) } as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Settings saved before they moved out of the versioned scope (newest version first). */
function legacySettingsKey(): string | null {
  try {
    for (let v = WORLD_VERSION - 1; v >= 1; v--) if (localStorage.getItem(`cloudpearl:v${v}:settings`)) return `cloudpearl:v${v}:settings`;
  } catch {
    /* storage unavailable */
  }
  return null;
}

function readNum(key: string, prefix = PREFIX): number | null {
  try {
    const raw = localStorage.getItem(prefix + key);
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function write(key: string, value: string, prefix = PREFIX): void {
  try {
    localStorage.setItem(prefix + key, value);
  } catch {
    /* storage unavailable — scores just won't persist */
  }
}

const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
export const DEFAULT_SETTINGS: SettingsState = { sound: true, music: true, quality: 'auto', speedFx: !reducedMotion, cap60: true, pearl: '#fdfaf6' };

export const storage = {
  settings(): SettingsState {
    let key = PREFS + 'settings';
    try {
      if (!localStorage.getItem(key)) key = legacySettingsKey() ?? key;
    } catch {
      /* storage unavailable */
    }
    return read(key, DEFAULT_SETTINGS);
  },
  saveSettings(s: SettingsState): void {
    write('settings', JSON.stringify(s), PREFS);
  },
  best(map: MapId, code: string): number | null {
    return readNum(`best:${map}:${normalizeSeedCode(code)}`);
  },
  setBest(map: MapId, code: string, meters: number): void {
    write(`best:${map}:${normalizeSeedCode(code)}`, String(Math.floor(meters)));
  },
  /** Input log of the best run on a seed — enough to verify or replay it later. */
  setReplay(map: MapId, code: string, data: string): void {
    write(`replay:${map}:${normalizeSeedCode(code)}`, data);
  },
  lastMap(): MapId | null {
    try {
      const v = localStorage.getItem(PREFS + 'lastMap');
      return v === 'sky' || v === 'dunes' ? v : null;
    } catch {
      return null;
    }
  },
  setLastMap(m: MapId): void {
    write('lastMap', m, PREFS);
  },
  /** Runs ever started (any world version) — paces the first-run control hints. */
  runs(): number {
    return readNum('runs', PREFS) ?? readNum('runs') ?? 0;
  },
  addRun(): void {
    write('runs', String(this.runs() + 1), PREFS);
  },
};
